import { useMemo } from 'react';

import type { CountryCode } from '~/api/common/types';
import type { UserLanguage } from '~/api/user/types';

import { useLanguageContext } from '~/providers/LanguageProvider/LanguageProvider.context';

/**
 * Collection of helper functions of the Intl namespace,
 * with automatic locale based on the authenticated user or the chosen app language.
 */
export const useIntl = (languageOverride?: UserLanguage) => {
  const { language } = useLanguageContext();

  const locale = (
    {
      nl: 'nl-BE',
      fr: 'fr-BE',
      en: 'en-GB',
    } as const
  )[languageOverride ?? language];

  // NumberFormat
  const decimalFormatter = useMemo(() => new Intl.NumberFormat(locale, { style: 'decimal', maximumFractionDigits: 2 }), [locale]);
  const currencyFormatter = useMemo(() => new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }), [locale]);
  const shortCurrencyFormatter = useMemo(() => new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }), [locale]); // prettier-ignore
  const percentageFormatter = useMemo(() => new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 2 }), [locale]);
  const shortPercentageFormatter = useMemo(() => new Intl.NumberFormat(locale, { style: 'percent' }), [locale]);

  // DateTimeFormat
  const dateFormatter = useMemo(() => new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }), [locale]);
  const shortDateFormatter = useMemo(() => new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }), [locale]);

  // RelativeTimeFormat
  const relativeTimeFormat = useMemo(() => new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'long' }), [locale]);

  // ListFormat
  const conjunctiveListFormatter = useMemo(() => new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }), [locale]);

  // Collator
  const sortCollator = useMemo(() => new Intl.Collator(locale, { usage: 'sort' }), [locale]);

  // Segmenter
  const graphemeSegmenter = useMemo(() => new Intl.Segmenter(locale, { granularity: 'grapheme' }), [locale]);

  // DisplayNames
  const regionDisplayNames = useMemo(() => new Intl.DisplayNames(locale, { type: 'region', fallback: 'code' }), [locale]);

  return useMemo(
    () => ({
      locale,
      formatDecimal: (...args: Parameters<typeof decimalFormatter.format>) => decimalFormatter.format(...args),
      formatCurrency: (...args: Parameters<typeof currencyFormatter.format>) => currencyFormatter.format(...args),
      formatCurrencyShort: (...args: Parameters<typeof shortCurrencyFormatter.format>) => shortCurrencyFormatter.format(...args),
      formatPercentage: (...args: Parameters<typeof percentageFormatter.format>) => percentageFormatter.format(...args),
      formatPercentageShort: (...args: Parameters<typeof shortPercentageFormatter.format>) => shortPercentageFormatter.format(...args),
      formatDate: (date: string | Date) => dateFormatter.format(new Date(date)),
      formatDateShort: (date: string | Date) => shortDateFormatter.format(new Date(date)),
      formatRelativeTime: (...args: Parameters<typeof relativeTimeFormat.format>) => relativeTimeFormat.format(...args),
      formatConjunctiveList: (...args: Parameters<typeof conjunctiveListFormatter.format>) => conjunctiveListFormatter.format(...args),
      compareForSort: (...args: Parameters<typeof sortCollator.compare>) => sortCollator.compare(...args),
      segmentGraphemes: (...args: Parameters<typeof graphemeSegmenter.segment>) => graphemeSegmenter.segment(...args),
      displayNameOfCountry: (countryCode: CountryCode) => regionDisplayNames.of(countryCode) ?? '',
    }),
    [
      locale,
      decimalFormatter,
      currencyFormatter,
      shortCurrencyFormatter,
      percentageFormatter,
      shortPercentageFormatter,
      dateFormatter,
      shortDateFormatter,
      relativeTimeFormat,
      conjunctiveListFormatter,
      sortCollator,
      graphemeSegmenter,
      regionDisplayNames,
    ],
  );
};
